const ACQUISITION_CONTEXT_KEY = "humblytics_acquisition_context";

const TRACKED_QUERY_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "gclid",
  "fbclid",
  "msclkid",
  // Forwarded by the LP cross-domain linker (humblytics-lp/src/lib/cross-domain-linker.ts).
  // The LP builds these on humblytics.com; they cannot reach us as cookies because
  // app.humblytics.com is a separate origin, so they arrive as query params.
  "_hmbl_fbc",
  "_hmbl_fbp",
  "_hmbl_lid",
  "source_page",
  "source_cta",
  // The marketing site's /get-started answer (growth | agency | scale | early).
  "fit",
  // The path picked on the marketing site: "agent" skips onboarding's agent-or-app question.
  "path",
  // Agent sign-ups: which agent tab was picked and the site typed on the marketing site.
  "agent",
  "site",
  "ref",
  "referrer",
] as const;

type AcquisitionContext = Record<string, string>;

function readStoredContext(): AcquisitionContext {
  try {
    const stored = localStorage.getItem(ACQUISITION_CONTEXT_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredContext(context: AcquisitionContext): void {
  try {
    localStorage.setItem(ACQUISITION_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Best-effort attribution only.
  }
}

function getCookieValue(name: string): string | undefined {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [key, ...valueParts] = cookie.split("=");
    if (key?.trim() === name) {
      const value = valueParts.join("=").trim();
      if (!value) return undefined;
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }
  return undefined;
}

function getHumblyticsViewId(): string | undefined {
  const sessionRef = getCookieValue("session_rev_ref");
  if (sessionRef) return sessionRef;

  const humblytics = (window as any).Humblytics;
  if (humblytics?.viewId) return String(humblytics.viewId);

  return undefined;
}

export function captureAcquisitionContext(
  from?: string,
): AcquisitionContext {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const context: AcquisitionContext = {};

  for (const key of TRACKED_QUERY_PARAMS) {
    const value = params.get(key);
    if (value) context[key] = value;
  }

  // When the path was picked (stored, not inferred from the latest landing URL: the
  // OTP and Google returns re-capture on other pages). See getSignupPath.
  if (context.path) context.path_at = String(Date.now());

  if (from) context.auth_from = from;
  if (document.referrer) {
    context.document_referrer = document.referrer;
    try {
      context.referrer_host = new URL(document.referrer).host;
    } catch {
      // Ignore invalid referrers.
    }
  }

  const humblyticsViewId = getHumblyticsViewId();
  if (humblyticsViewId) {
    context.humblytics_view_id = humblyticsViewId;
    context.session_rev_ref = humblyticsViewId;
  }

  context.auth_landing_path = window.location.pathname;
  context.auth_landing_url = window.location.href;

  const merged = {
    ...readStoredContext(),
    ...context,
  };
  writeStoredContext(merged);
  return merged;
}

const SIGNUP_PATH_TTL_MS = 60 * 60 * 1000;

/**
 * The marketing site's path choice for this sign-up ("agent" | "app"), or null.
 * Honoured for an hour after the link was opened, so an old visit's stored
 * `path` never steers a later sign-up from another link.
 */
export function getSignupPath(): "agent" | "app" | null {
  const { path, path_at } = readStoredContext();
  if (path !== "agent" && path !== "app") return null;
  const at = Number(path_at);
  return Number.isFinite(at) && Date.now() - at < SIGNUP_PATH_TTL_MS ? path : null;
}

export function getAcquisitionContext(): AcquisitionContext {
  if (typeof window === "undefined") return {};
  const context = readStoredContext();
  const humblyticsViewId = getHumblyticsViewId();
  if (!humblyticsViewId) return context;
  return {
    ...context,
    humblytics_view_id: humblyticsViewId,
    session_rev_ref: humblyticsViewId,
  };
}

/**
 * Resolve Meta's click/browser match keys for a Conversions API signup event.
 *
 * These are what let Meta join a conversion back to the ad click that caused it.
 * Without `fbc` the event lands unattributed, which is why paid campaigns can
 * report clicks but zero conversions.
 *
 * Resolution order, most to least reliable:
 *   1. `_hmbl_fbc` / `_hmbl_fbp` — forwarded by the LP linker, persisted here on
 *      the auth landing. This is the normal path for real ad traffic.
 *   2. The `_fbc` / `_fbp` cookies — only exist if a Meta base pixel ever ran on
 *      this origin. It does not today (no ad pixel in the product app), so treat
 *      this as a fallback for future setups rather than a live source.
 *   3. Rebuild `fbc` from a raw `fbclid` in Meta's documented `fb.1.{ts}.{fbclid}`
 *      shape, for the case where someone lands on the app directly from an ad.
 */
export function getMetaMatchKeys(): { fbc?: string; fbp?: string } {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const stored = readStoredContext();
  const pick = (key: string): string | undefined =>
    params.get(key) || stored[key] || undefined;

  let fbc = pick("_hmbl_fbc") || getCookieValue("_fbc");
  const fbp = pick("_hmbl_fbp") || getCookieValue("_fbp");

  if (!fbc) {
    const fbclid = pick("fbclid");
    if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  }

  // Meta rejects malformed fbc and it drags down Event Match Quality. Only send
  // a value that matches the documented shape.
  if (fbc && !/^fb\.1\.\d+\.[\w-]+$/.test(fbc)) fbc = undefined;

  return { ...(fbc ? { fbc } : {}), ...(fbp ? { fbp } : {}) };
}
